/**
 * Modo "panel inyectado" (editor en la pestaña de la app).
 *
 * La app es la pestaña top-level (login real) y el editor de Veo se inyecta
 * ENCIMA como una tarjeta flotante **arrastrable y redimensionable** (un iframe
 * al dashboard). Además se puede **sacar a una ventana independiente** del
 * navegador ("pop-out").
 *
 * El panel corre en el origen del dashboard (sesión de Veo para guardar guías) y
 * habla con la app (`window.parent` si está anclado, `window.opener` si está en
 * ventana aparte) por el puente postMessage. Este módulo inyecta/gestiona el
 * panel y expone su ventana destino.
 */
import { hasDocument, hasWindow } from '../../utils/safe-env';

const DEFAULT_PANEL_PATH = '/guides/design';
const PANEL_W = 400;
const PANEL_H = 640;
const MIN_W = 320;
const MIN_H = 320;
const MARGIN = 16;

export interface BuilderPanelOptions {
  dashboardOrigin: string;
  token: string;
  appOrigin: string;
  guide: string | null;
  panelPath: string | null;
  onClose: () => void;
}

export interface BuilderPanelHandle {
  /** Ventana destino de los mensajes: el iframe anclado o la ventana pop-out. */
  target(): Window | null;
  /**
   * Rect del IFRAME del panel en coordenadas de la página de la app, o `null`
   * si el editor está en pop-out (ventana aparte: no hay mapeo de coordenadas).
   * Lo usa el drag de bloques para convertir el puntero panel → página.
   */
  frameRect(): DOMRect | null;
  /** Quita el panel del DOM (y cierra la ventana pop-out si hay). */
  teardown(): void;
}

/** Inyecta el panel del editor (arrastrable, redimensionable, pop-out). */
export function injectBuilderPanel(opts: BuilderPanelOptions): BuilderPanelHandle {
  if (!hasWindow() || !hasDocument()) {
    return { target: () => null, frameRect: () => null, teardown: () => {} };
  }

  const panelUrl = buildPanelUrl(opts);

  const host = document.createElement('div');
  host.setAttribute('data-veo-builder-panel', '');
  const shadow = host.attachShadow({ mode: 'open' });

  const style = document.createElement('style');
  style.textContent = PANEL_CSS;

  const card = document.createElement('div');
  card.className = 'veo-panel';
  let left = Math.max(MARGIN, window.innerWidth - PANEL_W - MARGIN);
  let top = MARGIN;
  card.style.left = `${left}px`;
  card.style.top = `${top}px`;
  card.style.width = `${PANEL_W}px`;
  card.style.height = `${PANEL_H}px`;

  // Header: título + pop-out + cerrar.
  const header = document.createElement('div');
  header.className = 'veo-panel-header';
  const title = document.createElement('span');
  title.className = 'veo-panel-title';
  title.textContent = 'OnUser · Editor';
  const actions = document.createElement('div');
  actions.className = 'veo-panel-actions';
  const popout = document.createElement('button');
  popout.className = 'veo-panel-btn';
  popout.type = 'button';
  popout.title = 'Abrir en ventana aparte';
  popout.setAttribute('aria-label', 'Abrir en ventana aparte');
  popout.textContent = '⧉';
  const close = document.createElement('button');
  close.className = 'veo-panel-btn veo-panel-close';
  close.type = 'button';
  close.title = 'Cerrar';
  close.setAttribute('aria-label', 'Cerrar');
  close.textContent = '✕';
  actions.append(popout, close);
  header.append(title, actions);

  const iframe = document.createElement('iframe');
  iframe.className = 'veo-panel-frame';
  iframe.title = 'Editor de Veo';
  iframe.src = panelUrl;

  const grip = document.createElement('div');
  grip.className = 'veo-panel-resize';
  grip.setAttribute('aria-hidden', 'true');

  card.append(header, iframe, grip);
  shadow.append(style, card);
  document.body.appendChild(host);

  // ── Pop-out: sacar el editor a una ventana independiente ────────────────
  let popped: Window | null = null;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const stopPoll = (): void => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  close.addEventListener('click', () => opts.onClose());
  popout.addEventListener('click', () => {
    const win = window.open(
      panelUrl,
      'veo-editor',
      `popup,width=${Math.round(card.offsetWidth)},height=${Math.round(card.offsetHeight) + 60}`,
    );
    if (!win) return; // bloqueado por el navegador
    popped = win;
    card.style.display = 'none'; // el editor ahora vive en la ventana aparte
    pollTimer = setInterval(() => {
      if (popped?.closed) {
        stopPoll();
        opts.onClose(); // cerrar la ventana = salir del modo editor
      }
    }, 600);
  });

  // ── Arrastre por el header ──────────────────────────────────────────────
  header.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement)?.classList.contains('veo-panel-btn')) return;
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const ol = left;
    const ot = top;
    iframe.style.pointerEvents = 'none';
    const move = (ev: MouseEvent): void => {
      left = clamp(ol + (ev.clientX - sx), 0, Math.max(0, window.innerWidth - card.offsetWidth));
      top = clamp(ot + (ev.clientY - sy), 0, Math.max(0, window.innerHeight - 40));
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      iframe.style.pointerEvents = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });

  // ── Redimensionar por el grip (esquina inferior derecha) ────────────────
  grip.addEventListener('mousedown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const sw = card.offsetWidth;
    const sh = card.offsetHeight;
    iframe.style.pointerEvents = 'none';
    const move = (ev: MouseEvent): void => {
      card.style.width = `${clamp(sw + (ev.clientX - sx), MIN_W, window.innerWidth)}px`;
      card.style.height = `${clamp(sh + (ev.clientY - sy), MIN_H, window.innerHeight)}px`;
    };
    const up = (): void => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      iframe.style.pointerEvents = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });

  return {
    target: () => (popped && !popped.closed ? popped : iframe.contentWindow),
    frameRect: () => (popped && !popped.closed ? null : iframe.getBoundingClientRect()),
    teardown: () => {
      stopPoll();
      if (popped && !popped.closed) popped.close();
      host.remove();
    },
  };
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

function buildPanelUrl(opts: BuilderPanelOptions): string {
  const path = opts.panelPath ?? DEFAULT_PANEL_PATH;
  const url = new URL(path, opts.dashboardOrigin);
  url.searchParams.set('veoToken', opts.token);
  url.searchParams.set('veoApp', opts.appOrigin);
  if (opts.guide) url.searchParams.set('veoGuide', opts.guide);
  return url.toString();
}

const PANEL_CSS = `
:host { all: initial; }
.veo-panel {
  position: fixed;
  min-width: ${MIN_W}px; min-height: ${MIN_H}px;
  max-width: 96vw; max-height: 94vh;
  display: flex; flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.28);
  overflow: hidden;
  z-index: 2147483647;
  font-family: system-ui, -apple-system, sans-serif;
}
.veo-panel-header {
  flex: 0 0 auto;
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px 8px 12px;
  background: #1b1714; color: #f7f1eb;
  font-size: 13px; font-weight: 600;
  cursor: grab;
}
.veo-panel-header:active { cursor: grabbing; }
.veo-panel-actions { display: flex; gap: 2px; }
.veo-panel-btn {
  border: 0; background: transparent; color: #fff;
  font-size: 14px; cursor: pointer; line-height: 1;
  padding: 4px 7px; border-radius: 6px;
}
.veo-panel-btn:hover { background: rgba(255,255,255,0.15); }
.veo-panel-frame { flex: 1 1 auto; width: 100%; border: 0; background: #fff; }
.veo-panel-resize {
  position: absolute; right: 0; bottom: 0;
  width: 16px; height: 16px;
  cursor: nwse-resize;
  background:
    linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 60%,
    transparent 60%, transparent 72%, rgba(0,0,0,0.25) 72%, rgba(0,0,0,0.25) 82%, transparent 82%);
}
`;
